import React, { FC, useState, KeyboardEvent } from 'react'
import { Box, Chip, IconButton, TextField, Tooltip, keyframes, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import SendIcon from '@mui/icons-material/Send'
import StopIcon from '@mui/icons-material/Stop'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
`

type MessageInputProps = {
    onSend: (content: string) => void
    disabled: boolean
    suggestions?: string[]
}

const MessageInput: FC<MessageInputProps> = ({ onSend, disabled, suggestions = [] }) => {
    const theme = useTheme()
    const [value, setValue] = useState('')
    const [focused, setFocused] = useState(false)

    const handleSend = () => {
        const trimmed = value.trim()
        if (!trimmed || disabled) return
        onSend(trimmed)
        setValue('')
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }

    const showSuggestions = suggestions.length > 0 && !value && !disabled

    return (
        <Box
            sx={{
                borderTop: 1,
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.background.paper, 0.85),
                backdropFilter: 'blur(12px)',
            }}
        >
            {showSuggestions && (
                <Box
                    sx={{
                        display: 'flex',
                        gap: 0.75,
                        px: 2,
                        pt: 1.5,
                        pb: 0.5,
                        overflowX: 'auto',
                        animation: `${fadeIn} 0.2s ease`,
                        '&::-webkit-scrollbar': { display: 'none' },
                    }}
                >
                    {suggestions.map((s) => (
                        <Chip
                            key={s}
                            label={s}
                            size="small"
                            variant="outlined"
                            onClick={() => onSend(s)}
                            sx={{
                                flexShrink: 0,
                                borderRadius: 3,
                                borderColor: 'divider',
                                fontSize: '0.75rem',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: 'primary.lighter',
                                    borderColor: 'primary.main',
                                    transform: 'translateY(-1px)',
                                },
                            }}
                        />
                    ))}
                </Box>
            )}

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 1,
                    p: 2,
                    pt: showSuggestions ? 1 : 2,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'flex-end',
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: focused ? 'primary.main' : 'divider',
                        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                        boxShadow: focused ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}` : 'none',
                        px: 1,
                    }}
                >
                    <AutoAwesomeIcon
                        sx={{
                            fontSize: 18,
                            color: focused ? 'primary.main' : 'grey.400',
                            mb: 1.2,
                            ml: 0.5,
                            transition: 'color 0.2s ease',
                        }}
                    />
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        placeholder="Pergunte à Cléo sobre ativos, doses, pH…"
                        value={value}
                        onChange={(e) => setValue(e.target.value.slice(0, 5000))}
                        onKeyDown={handleKeyDown}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        disabled={disabled}
                        size="small"
                        variant="standard"
                        sx={{
                            '& .MuiInputBase-root': {
                                py: 0.75,
                                px: 0.5,
                            },
                            '& .MuiInput-underline:before': { display: 'none' },
                            '& .MuiInput-underline:after': { display: 'none' },
                        }}
                    />
                </Box>
                <Tooltip title={disabled ? "Parar" : "Enviar"} placement="top">
                    <span>
                        <IconButton
                            onClick={handleSend}
                            disabled={!disabled && !value.trim()}
                            aria-label={disabled ? "Parar" : "Enviar"}
                            sx={{
                                width: 42,
                                height: 42,
                                bgcolor: disabled ? 'error.main' : value.trim() ? 'primary.main' : 'action.disabledBackground',
                                color: '#fff',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: disabled ? 'error.dark' : 'secondary.main',
                                    transform: 'scale(1.05)',
                                },
                                '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
                            }}
                        >
                            {disabled ? <StopIcon fontSize="small" /> : <SendIcon fontSize="small" />}
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    )
}

export default MessageInput